import { Injectable } from "@angular/core";
import {Actions, createEffect, ofType} from '@ngrx/effects';
import { Action, Store } from "@ngrx/store";
import { Observable, of } from 'rxjs';
import { map, catchError, withLatestFrom, mergeMap, exhaustMap, switchMap } from 'rxjs/operators';
import { PageNotificationService, LanguageService } from "@signature-it/ngx-generic/services";
import { GroupApiService, UtilityService, SolrProductService, FavoritesService } from "@signature-it/ngx-catalogue/services";
import { CatalogueActions } from "@signature-it/ngx-catalogue/store/actions";
import {PriceDTO, StockDTO, Group, ProductFullSolrDTO, GroupUserLinksFields, FavoritesDTO} from '@signature-it/ngx-catalogue/models';
import { ProductService } from '@signature-it/ngx-catalogue/store/services';
import * as fromSelectors from "@signature-it/ngx-catalogue/store/selectors";

declare let _: any;

@Injectable()
export class CatalogueEffects {

    constructor(
        protected notifySvc: PageNotificationService,
        protected store$: Store<any>,
        protected actions$: Actions,
        protected productSvc: ProductService,
        protected groupSvc: GroupApiService,
        protected utilitySvc: UtilityService,
        protected langSvc: LanguageService,
        protected solrSvc: SolrProductService,
        protected favoritesSvc: FavoritesService) {}

    getProductPrice$=  createEffect(() => this.actions$
        .pipe(ofType(CatalogueActions.GET_PRODUCT_PRICE),
            withLatestFrom(this.store$.select(fromSelectors.getCataloguePriceList)),
            mergeMap(([state, priceList]) => {
                if (priceList && state['payload'] && (<any>state['payload']).length) {
                    const foundPrices = _.filter(priceList, (itm) => {
                        return (<any>state['payload']).indexOf(Number(itm.id)) > -1;
                    });
                    if (foundPrices.length === (<any>state['payload']).length) {
                        return of(CatalogueActions.gotProductPriceSuccess(foundPrices));
                    }
                }
                return this.productSvc.getPrice(state['payload']).pipe(
                    map((data: PriceDTO[]) => CatalogueActions.gotProductPriceSuccess(data)),
                    catchError(error => of(CatalogueActions.gotProductPriceFailure()))
                );
            })));

    getProductsStock$=  createEffect(() => this.actions$
        .pipe(ofType(CatalogueActions.GET_PRODUCTS_STOCK),
            withLatestFrom(this.store$.select(fromSelectors.getCatalogueStockList)),
            mergeMap(([state, stockList]) => {
                if (stockList && state['payload'] && (<any>state['payload']).length) {
                    const foundStocks = _.filter(stockList, (itm) => {
                        return (<any>state['payload']).indexOf(Number(itm.id)) > -1;
                    });
                    if (foundStocks.length === (<any>state['payload']).length) {
                        return of(CatalogueActions.gotProductsStockSuccess(foundStocks));
                    }
                }
                return this.productSvc.getStock(state['payload']).pipe(
                    map((data:StockDTO[]) => CatalogueActions.gotProductsStockSuccess(data)),
                    catchError(error => of(CatalogueActions.gotProductsStockFailure()))
                )
            })));

    getGroupUserLinks=  createEffect(() => this.actions$
        .pipe(ofType(CatalogueActions.GET_GROUP_USER_LINKS),
            withLatestFrom(this.store$.select(fromSelectors.getGroupUserLinksFields)),
            mergeMap(([state, groupUserLinksFields]) => {
                if (groupUserLinksFields && state['payload']) {
                    const taxonomy = state['payload']['taxonomy'];
                    const entityVar = state['payload']['entityVar'];
                    let filterObj;
                    if (!taxonomy && !entityVar) {
                        filterObj = {id: state['payload']['groupId'], taxonomy: '', entityVar: ''};
                    } else {
                        filterObj = {taxonomy: state['payload']['taxonomy'], entityVar: state['payload']['entityVar']};
                    }
                    let groupUserLF = _.filter(groupUserLinksFields, filterObj);
                    if (groupUserLF.length) {
                        return of(CatalogueActions.gotGroupUserLinksSuccess(groupUserLF));
                    }
                }
                return this.groupSvc.getUserLinksFields(state['payload']).pipe(
                    map((data: GroupUserLinksFields) => CatalogueActions.gotGroupUserLinksSuccess([data])),
                    catchError(error => of(CatalogueActions.gotGroupUserLinksFailure()))
                );
            })));

    getGroupsTree=  createEffect(() => this.actions$
        .pipe(ofType(CatalogueActions.GET_GROUPS_TREE),
            withLatestFrom(this.store$.select(fromSelectors.getGroupsTree)),
            mergeMap(([state, getGroupsTree]) => {
                const taxonomy = state['payload'];
                if (getGroupsTree) {
                    if ((taxonomy && getGroupsTree['taxonomyUri'] == taxonomy) || (!taxonomy && !getGroupsTree['taxonomyUri'])) {
                        return of(CatalogueActions.gotGroupsTreeSuccess(getGroupsTree));
                    }
                }
                return this.groupSvc.getGroupsTree(state['payload']).pipe(
                    map((groupsTree: Group) => CatalogueActions.gotGroupsTreeSuccess(groupsTree)),
                    catchError(error => of(CatalogueActions.gotGroupsTreeFailure()))
                );
            })));

    getCurrentGroup =  createEffect(() => this.actions$
        .pipe(ofType(CatalogueActions.GET_CURRENT_GROUP),
            withLatestFrom(this.store$.select(fromSelectors.getGroupsTree)),
            mergeMap(([state, getGroupsTree]) => {
                let g = this.utilitySvc.findDeep(getGroupsTree, {id : state['payload']['groupId']}, getGroupsTree.id);
                if (!_.isEmpty(g)) {
                    if (!state['payload']['getAdditionalData'] || !g.taxonomyVar || g.requestedDoc) return of(CatalogueActions.gotCurrentGroupSuccess(g));
                    return this.groupSvc.getAdditionalData(g).pipe(
                        map(docs => {
                            g.requestedDoc = true;
                            docs = docs?.['response']?.['docs'] || [];
                            if (docs.length) {
                                if (g.isleaf) {
                                    g.doc = docs[0]
                                } else {
                                    g.children.filter(c => !c.doc).forEach((child: Group) => {
                                        child.doc = docs.find((d) => d.uri_s === child.entityUri);
                                        if (child.isleaf && child.doc) {
                                            child.requestedDoc = true;
                                        }
                                    });
                                    if (!g.doc) {
                                        g.doc = docs.find((d) => d.uri_s === g.entityUri);
                                    }
                                }
                            }
                            this.store$.dispatch(CatalogueActions.gotGroupsTreeSuccess(getGroupsTree));
                            return CatalogueActions.gotCurrentGroupSuccess(g)
                        }))
                } else {
                    return of(CatalogueActions.gotCurrentGroupFailure());
                }})));

    getProductSolrDoc=  createEffect(() => this.actions$
        .pipe(ofType(CatalogueActions.GET_PRODUCT_SOLR_DOC),
            withLatestFrom(this.store$.select(fromSelectors.getProductSolrDoc)),
            mergeMap(([state, productSolrDoc]) => {
                if (productSolrDoc && state['payload']) {
                    if(productSolrDoc && productSolrDoc['product_id_i'] == state['payload']) {
                        return of(CatalogueActions.gotProductSolrDocSuccess(productSolrDoc));
                    }
                }

                const lang = this.langSvc.getCurrentLanguage();
                return this.solrSvc.getSolrByProductIds(lang, [state['payload']]).pipe(
                    map((data: ProductFullSolrDTO[]) => CatalogueActions.gotProductSolrDocSuccess(data[0])),
                    catchError(error => of(CatalogueActions.gotProductSolrDocFailure()))
                );
            })));

    getFavorites=  createEffect(() => this.actions$
        .pipe(ofType(CatalogueActions.GET_FAVORITES),
            withLatestFrom(this.store$.select(fromSelectors.getCatalogueState)),
            exhaustMap(([state, catalogueState]) => {
                if (catalogueState.favoritesList && !catalogueState.processingAddToFavorites) return of(CatalogueActions.getFavoritesSuccess(catalogueState.favoritesList));
                return this.favoritesSvc.getFavoriteList().pipe(
                    map((data: FavoritesDTO[]) => CatalogueActions.getFavoritesSuccess(data)),
                    catchError(error => of(CatalogueActions.getFavoritesFailure()))
                );
            })));

    addFavorite=  createEffect(() => this.actions$
        .pipe(ofType(CatalogueActions.ADD_FAVORITE),
            map((action) => action['payload']),
            switchMap((payload) => {
                return this.favoritesSvc.addFavorites(payload).pipe(
                    map((data) => {
                        this.store$.dispatch(CatalogueActions.getFavorites());
                        return CatalogueActions.addFavoriteSuccess(payload);
                    }),
                    catchError(error => of(CatalogueActions.addFavoriteFailure()))
                );
            })));

    removeFavorite=  createEffect(() => this.actions$
        .pipe(ofType(CatalogueActions.REMOVE_FAVORITE),
            map((action) => action['payload']),
            switchMap((payload) => {
                return this.favoritesSvc.removeFavorites(payload).pipe(
                    map((data) => CatalogueActions.removeFavoriteSuccess(payload)),
                    catchError(error => of(CatalogueActions.removeFavoriteFailure()))
                );
            })));
}


